<template>
  <div class="controls">
    <div class="controls__start experiment-header">
      <div class="d-flex justify-content-between align-items-center">
        <span id="experiment-header-title" class="controls__count">{{ currentExperiment ? currentExperiment.name : '' }}</span>
        <EncodedIdField
          :id="expID"
          :type="'experiment'"
          :hideLabel="true"
          class="ml-2"
        />
      </div>
      <span v-if="loading" class="controls__count">Validate Sessions</span>
      <span v-else class="controls__count">Validate Sessions {{ validateSessions ? `(${validateSessions.length})` : '' }}</span>
    </div>
    <div class="controls__middle">
      <HelpTip class="ml-2">
        Validate session list displays both ongoing and previous sessions associated with a specific experiment.
        You can view details, metrics, and logs by selecting any session card.
        To compare multiple validate sessions within the experiment, click "COMPARE".
      </HelpTip>
    </div>
    <div class="controls__end">
      <div class="controls__actions">
        <button id="compare-btn" class="button" @click="handleCompareSessionsClicked">
          <SVGIcon
            :iconName="'chart_multiple'"
            :width="'18px'"
            :height="'18px'"
          />Compare
        </button>
        <ActionsButton
          v-if="permissions && permissions.trainer_write"
          label="Actions"
          :actions="validatorActions"
          :showCount="true"
          :selectedCount="selectedCount"
          @action-clicked="handleValidatorActionClick"
        />
      </div>
    </div>
  </div>
  <div v-if="loading" class="empty-state">
    <div id="loading-visualization"><div class="lds-ring"><div /><div /><div /><div /></div></div>
  </div>
  <template v-else>
    <div v-if="validateSessions && validateSessions.length > 0" class="validator-card-container scrollbar">
      <ValidatorCard
        v-for="(s, i) in validateSessions"
        :key="i"
        :session="s"
        :permissions="permissions"
        :trainingSessions="trainingSessions"
        :selected="selectedSessions[s.id] || false"
        @show-charts="handleSessionChartsClicked(s)"
        @delete-session="openConfirmDeleteValidation(s)"
        @refresh-session="handleRefreshSession(s)"
        @info-clicked="handleSessionInfoClicked(s)"
        @session-selected="handleSessionSelected"
      />
    </div>
    <div
      v-else
      class="empty-state"
    >
      <EmptyState
        :title="'No validation sessions found for this experiment'"
        :subtitle="'To add a new validation session, click the New action'"
      />
    </div>
  </template>

  <StartValidatorModal
    ref="startValidatorModal"
    :datasets="datasets"
    :snapshots="snapshots"
    @validate-session-started="handleValidateSessionCreated"
  />
  <ValidationSessionEditDialog
    ref="validationSessionEditDialog"
    :selectedSessions="validateSessions"
    @edit-session="handleEditSessionConfirmed"
  />
  <ConfirmModal
    ref="confirmModal"
    :messageHeader="confirmMessageHeader"
    :message="confirmMessage"
    :buttonClass="'button-delete'"
    :buttonText="buttonText"
    @confirmed="confirmFunction"
  />
  <CompareValidateSessionModal
    ref="compareValidateSessionsModal"
    :sessions="validateSessions"
    @open-compare="(sessionIds) => handleOpenCompareSessionsPage(sessionIds)"
  />
</template>

<script>
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import ConfirmModal from '@/components/ConfirmModal.vue';
import StartValidatorModal from '@/components/ValidationPage/StartValidatorModal.vue';
import ValidatorCard from '@/components/ValidationPage/ValidatorCardV2.vue';
import ValidateSessionDetailsModal from '@/components/ValidationPage/ValidateSessionDetailsModal.vue';
import ValidationSessionEditDialog from '@/components/ValidationPage/ValidationSessionEditDialog.vue';
import EncodedIdField from '@/components/EncodedIdField.vue';
import SVGIcon from '@/components/SVGIcon.vue';
import CompareValidateSessionModal from '@/components/TrainerComponent/TrainerExperimentAndSessions/CompareValidateSessionModal.vue';
import ActionsButton from '@/components/ActionsButton.vue';
import { useToast } from 'vue-toast-notification';
import HelpTip from '@/components/HelpTip.vue';
import EmptyState from '@/components/EmptyState.vue';
import 'vue-toast-notification/dist/theme-sugar.css';

const $toast = useToast();

export default {
  name: "ValidatorManagement",
  components: {
    ConfirmModal,
    StartValidatorModal,
    ValidatorCard,
    ValidateSessionDetailsModal,
    ValidationSessionEditDialog,
    EncodedIdField,
    HelpTip,
    EmptyState,
    SVGIcon,
    CompareValidateSessionModal,
    ActionsButton,
  },
  inject: {
    isPublic: { default: false },
  },
  props: {
  },
  emits: [
    'update-list',
  ],
  data() {
    return {
      dataConnect: new DatastoreConnect(this.$store.state.enterpriseServerUrl),
      selectedValidateSession: null,
      validateSessions: null,
      interval: null,
      confirmMessage: null,
      confirmMessageHeader: null,
      toDelete: null,
      confirmFunction: null,
      buttonText: 'Delete',
      permissions: null,
      loading: true,
      snapshots: [],
      trainingSessions: null,
      selectedSessions: {},
      validatorActions: [
        { icon: 'add', label: 'New', eventKey: 'new', tooltip: 'Create New Validation Session' },
        { icon: 'edit', label: 'Edit', eventKey: 'edit', tooltip: 'Edit selected validation session' },
        { icon: 'delete', label: 'Delete', eventKey: 'delete', isAlert: true, tooltip: 'Delete selected validation sessions' },
      ],
    };
  },
  computed: {
    currentProject() {
      return this.$store.state.projects.currentProject;
    },
    currentExperiment: {
      get() {
        return this.$store.state.experiments.currentExperiment;
      },
      set(exp) {
        this.$store.commit('experiments/setCurrentExperiment', exp);
      },
    },
    currentValidateSession: {
      get() {
        return this.$store.state.experiments.currentValidateSession;
      },
      set(session) {
        this.$store.commit('experiments/setCurrentValidateSession', session);
      },
    },
    datasets() {
      return this.$store.state.datasets.datasetList;
    },
    notEmpty() {
      return this.validateSessions && this.validateSessions.length > 0;
    },
    selectedCount() {
      return Object.values(this.selectedSessions).filter(Boolean).length;
    },
  },
  watch: {
    async currentExperiment(exp) {
      this.trainingSessions = await this.getTrainingSessionList(exp.id);
      this.getValidateSessions(exp.id);
    },
  },
  beforeUnmount() {
    this.stopInterval();
  },
  async mounted() {
    const queryExpId = parseInt(this.$route.query.exp_id);
    if (!queryExpId && this.currentExperiment) {
      this.$router.replace({ name: `${this.isPublic ? 'public.' : ''}experiment.validation.list`, query: { exp_id: this.currentExperiment.id } });
    }

    if (this.currentExperiment) {
      this.trainingSessions = await this.getTrainingSessionList(this.currentExperiment.id);
      this.getValidateSessions(this.currentExperiment.id);
    }

    await this.getProjectAccessForSingleUser();

    if (this.permissions && this.permissions.trainer_write) {
      this.snapshots = await this.getSnapshots();
    }
    this.startInterval();
  },
  methods: {
    handleCompareSessionsClicked() {
      this.$refs.compareValidateSessionsModal.showModal();
    },
    handleValidatorActionClick(eventKey) {
      if (eventKey === 'new') {
        this.openStartValidatorModal();
      } else if (eventKey === 'edit') {
        this.handleEditSelectedSessions();
      } else if (eventKey === 'delete') {
        this.handleDeleteSelectedSessions();
      }
    },
    handleSessionSelected({ sessionId, isSelected }) {
      this.selectedSessions[sessionId] = isSelected;
      this.$forceUpdate();
    },
    handleDeleteSelectedSessions() {
      const selectedIds = Object.keys(this.selectedSessions)
        .filter((id) => this.selectedSessions[id])
        .map((id) => parseInt(id, 10));
      if (selectedIds.length === 0) {
        $toast.open({
          queue: true,
          duration: 5000,
          message: 'Please select at least one session to delete',
          type: "warning",
          position: "top",
        });
        return;
      }
      this.confirmMessage = `Are you sure you want to move ${selectedIds.length} validation session(s) to recycle bin?`;
      this.confirmMessageHeader = 'Remove Sessions';
      this.buttonText = 'Remove';
      this.$refs.confirmModal.showModal();
      this.confirmFunction = () => {
        this.handleDeleteValidateSessions(selectedIds);
        this.selectedSessions = {};
      };
    },
    handleEditSelectedSessions() {
      const selectedIds = Object.keys(this.selectedSessions)
        .filter((id) => this.selectedSessions[id])
        .map((id) => parseInt(id, 10));
      if (selectedIds.length === 0) {
        $toast.open({
          queue: true,
          duration: 5000,
          message: 'Please select a validation session to edit',
          type: "warning",
          position: "top",
        });
        return;
      }
      if (selectedIds.length > 1) {
        $toast.open({
          queue: true,
          duration: 5000,
          message: 'Please select only one session to edit',
          type: "warning",
          position: "top",
        });
        return;
      }
      const selectedSession = this.validateSessions.find((s) => s.id === selectedIds[0]);
      if (selectedSession) {
        this.$refs.validationSessionEditDialog.openModal(selectedSession);
      }
    },
    handleEditSessionConfirmed(editedSession) {
      const index = this.validateSessions.findIndex((s) => s.id === editedSession.id);
      if (index > -1) {
        this.validateSessions[index] = { 
          ...this.validateSessions[index], 
          ...editedSession,
          docker_task: {
            ...this.validateSessions[index].docker_task,
            name: editedSession.name,
          }
        };
      }
      this.selectedSessions = {};
    },
    async getValidateSessions(id) {
      this.loading = true;
      this.validateSessions = await this.getValidateSessionList(id);
      this.loading = false;
    },
    async handleRefreshSession(session) {
      const index = this.validateSessions.findIndex((e) => e.id === session.id);
      if (index > -1) {
        this.validateSessions[index] = await this.getSession(session.id);
      }
    },
    async getProjectAccessForSingleUser() {
      if (this.currentProject && this.$store.state.user?.user?.username) {
        await this.dataConnect.getProjectAccessForSingleUser({
          project_id: this.currentProject.id,
          username: this.$store.state.user.user.username,
        })
          .then((data) => {
            if (data.result) {
              this.permissions = data.result;
            } else if (!data || data.error) {
              $toast.open({
                queue: true,
                duration: 5000,
                message: `Failed to retrieve project access: ${data.error.message}`,
                type: "error",
                position: "top",
              });
            }
          })
          .catch((e) => {
            $toast.open({
              queue: true,
              duration: 5000,
              message: `Failed to retrieve project access: ${e}`,
              type: "error",
              position: "top",
            });
          });
      }
    },
    async handleValidateSessionCreated() {
      await this.getValidateSessions();
    },
    stopInterval() {
      clearInterval(this.interval);
      this.interval = null;
    },
    startInterval() {
      this.interval = setInterval(async () => {
        await this.getValidateSessions();
      }, 30000);
    },
    async getValidateSessionList() {
      const resp = await this.dataConnect.getValidateSessionListByExperiment({ experiment_id: this.currentExperiment.id, reverse: true })
        .catch((error) => {
          $toast.open({
            queue: true,
            duration: 5000,
            message: `Failed to retrieve validate sessions: ${error}`,
            type: "error",
            position: "top",
          });
        });
      if (!resp || resp.error || !resp.result) {
        $toast.open({
          queue: true,
          duration: 5000,
          message: `Failed to retrieve validate sessions: ${resp.error}`,
          type: "error",
          position: "top",
        });
        return [];
      }
      if (!resp || resp.error || !resp.result) {
        return [];
      }
      return resp.result;
    },
    async getTrainingSessionList(trainer_id) {
      const resp = await this.dataConnect.getTrainingSessionList({ trainer_id, reverse: true })
        .catch((error) => {
          $toast.open({
            queue: true,
            dismissible: true,
            message: `Failed to retrieve training sessions: ${error}`,
            type: "error",
            position: "top",
          });
        });
      if (!resp || resp.error || !resp.result) {
        $toast.open({
          queue: true,
          dismissible: true,
          message: `Failed to retrieve training sessions: ${resp.error.message}`,
          type: "error",
          position: "top",
        });
        return [];
      }
      return resp.result;
    },
    async getSession(id) {
      const resp = await this.dataConnect.getValidateSession({
        validate_session_id: id,
      })
        .catch((e) => {
          $toast.open({
            queue: true,
            duration: 5000,
            message: `Failed to retrieve validate session: ${e}`,
            type: "error",
            position: "top",
          });
        });
      if (!resp || resp.error || !resp.result) {
        $toast.open({
          queue: true,
          duration: 5000,
          message: `Failed to retrieve validate session: ${resp.error}`,
          type: "error",
          position: "top",
        });
        return null;
      }
      return resp.result;
    },
    openStartValidatorModal(d) {
      this.$refs.startValidatorModal.showModal();
    },

    openConfirmDeleteValidation(session) {
      this.confirmMessage = `Are you sure you want to move this validate session to recycle bin?`;
      this.confirmMessageHeader = 'Remove Session';
      this.toDelete = session;
      this.buttonText = 'Remove';
      this.$refs.confirmModal.showModal();
      this.confirmFunction = () => {
        this.handleDeleteValidateSessions([session.id]);
      };
    },
    async handleDeleteValidateSessions(ids) {
      await this.dataConnect.deleteValidateSessions({
        session_ids: ids,
      })
        .then(async (data) => {
          if (!data || data.error) {
            $toast.open({
              queue: true,
              duration: 5000,
              message: `Failed to delete validate session: ${data.error.message}`,
              type: "error",
              position: "top",
            });
          }
          await this.getValidateSessions();
        })

        .catch((error) => {
          $toast.open({
            queue: true,
            duration: 5000,
            message: `Failed to delete validate session: ${error}`,
            type: "error",
            position: "top",
          });
        });

      this.toDelete = null;
    },
    async getSnapshots() {
      await this.dataConnect.listSnapshots()
        .then((data) => {
          if (data.result && !data.error) {
            this.snapshots = data.result.sort((a, b) => b.id - a.id);
          } else if (!data || data.error) {
            $toast.open({
              queue: true,
              duration: 5000,
              message: `Failed to get snapshots: ${data.error.message}`,
              type: "error",
              position: "top",
            });
          }
        })
        .catch((error) => {
          $toast.open({
            queue: true,
            duration: 5000,
            message: `Failed to get snapshots: ${error}`,
            type: "error",
            position: "top",
          });
        });
    },
    async handleGetExperiment(id) {
      await this.dataConnect.getExperiment({ trainer_id: id })
        .then(async (data) => {
          if (data.result) {
            this.currentExperiment = data.result;
          } else if (data.error) {
            $toast.open({
              queue: true,
              dismissible: true,
              message: `Failed to retrieve experiment: ${data.error.message}`,
              type: "error",
              position: "top",
            });
          }
        })
        .catch((error) => {
          $toast.open({
            queue: true,
            dismissible: true,
            message: `Failed to retrieve experiment: ${error}`,
            type: "error",
            position: "top",
          });
        });
    },
    handleSessionInfoClicked(session) {
      this.currentValidateSession = session;
      let trainSessionId = session.training_session_id;
      if (!trainSessionId) {
        trainSessionId = session.validate_params.train_session_id;
      }
      this.$router.push({ name: `${this.isPublic ? 'public.' : ''}experiment.validation.details`, query: { val_session_id: session.id } });
    },
    handleSessionChartsClicked(session) {
      this.currentValidateSession = session;
      let trainSessionId = session.training_session_id;
      if (!trainSessionId) {
        trainSessionId = session.validate_params.train_session_id;
      }
      this.$router.push({ name: `${this.isPublic ? 'public.' : ''}experiment.validation.details`, query: { val_session_id: session.id, mode: 'charts' } });
    },
    handleOpenCompareSessionsPage(sessionIds) {
      this.$router.push({ name: `${this.isPublic ? 'public.' : ''}experiment.validation.compare`, query: { exp_id: this.currentExperiment.id, session_ids: encodeURI(JSON.stringify(sessionIds)) } });
    },
  },
};
</script>

<style lang="scss" scoped>
.controls {
  display: flex;
  margin: 20px 20px 0 20px;
  align-items: center;
  gap: 10px;

  &__count {
    font-weight: bold;
    font-size: 1rem;
  }

  &__start {
    display: flex;
    justify-content: flex-start;
    width: fit-content;
    gap: 10px;

    &.experiment-header {
      flex-direction: column;
    }
  }

  &__end {
    display: flex;
    justify-content: flex-end;
    width: fit-content
  }

  &__middle {
    display: flex;
    justify-content: center;
    align-items: center;
    flex: 1;
  }

  &__actions {
    display: flex;
    flex-direction: row;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    height: 100%;
  }
}
@media (max-width: $breakpoint-sm) {
  .controls {
    flex-direction: column;
    &__start{
      display: flex;
      align-items: flex-start;
      flex-direction: column;
      width: 100%;
    }

    &__middle{
      display: flex;
      align-items: flex-start;
      flex-direction: column;
      width: 100%;
    }

    &__end{
      display: flex;
      align-items: flex-end;
      flex-direction: column;
      width: 100%;
    }
  }
}

.validator-header {
  display: flex;
  flex-direction: row;
  gap: 16px;
}

.validator-card-container {
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  gap: 16px;
  padding: 16px;
  overflow: auto;
}

.empty-state {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  width: 100%;
  height: 100%;
}

.button {
    background: white;
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: center;
    height: 26px;
    padding: 0;
    box-shadow: 1px 2px 4px 1px rgba(0, 0, 0, 0.2), 0 0 1px 1px rgba(0, 0, 0, 1);
    padding: 4px 8px;
    gap: 2px;
    width: fit-content;

    @include themify() {
      color: themed('body-text-color')
    }

    &.highlight {
      background: var(--button-color);
      color: var(--button-text-color);

      svg {
        margin-right: 5px;
      }
    }

    &:hover {
      background: rgba(var(--color-primary-100-rgb), 0.5);
    }

    &.delete:not([disabled]) {
      @include themify() {
        background: themed('button-delete');
        color: themed('button-delete-text');
      }
    }

    &.delete[disabled] {
      border: none;
    }

    &.delete:hover:not([disabled]) {
      @include themify() {
        background: $color-red-300;
        color: themed('button-delete-text');
      }
    }
  }
</style>
